﻿import { FLOW_STEPS, type FlowStep } from './flowReference';
// Overview's authored network demonstration is separate from the mission
// reference used by Knowledge and Maintenance.
export const STAGE_STORY: Record<number, FlowStep> = {
  ...Object.fromEntries(FLOW_STEPS.map((s) => [s.id, s])),
  2: {
    ...FLOW_STEPS[1],
    title: 'Direct downlink',
    detail: 'Jason-3 downlinks to the RF and optical antennas in this demonstration.',
    output: 'Signals at the receiving antennas',
    assets: [
      ...FLOW_STEPS[1].assets,
      {
        id: 'optical_antenna',
        label: 'Optical antenna',
        role: 'Tracking telescope and opening observatory dome',
      },
    ],
    process: [
      'Only the two satellite downlinks are shown. The field sources do not transmit in this step. The optical terminal opens its shutter and aligns before its downlink appears.',
      'Jason-3 is an observing satellite, not a collector relay. The optical path is conceptual; the mission reference below describes its actual RF downlink.',
      ...FLOW_STEPS[1].process,
    ],
  },
  3: {
    ...FLOW_STEPS[2],
    title: 'Relay observations via GOES',
    detail: 'Field sources and Jason-3 → GOES → RF antenna (conceptual).',
    input: 'Collected observations',
    action: 'Relay through GOES',
    output: 'Relayed signal at the RF antenna',
    note: 'Conceptual combined network; Jason-3 does not have an operational GOES relay link.',
    assets: [
      FLOW_STEPS[1].assets[0],
      { id: 'goes', label: 'GOES', role: 'Satellite relay in this demonstration' },
      FLOW_STEPS[1].assets[1],
    ],
    process: [
      'ER-2, the vessel, terrestrial station, field observer and Jason-3 each transmit directly toward GOES in this conceptual scene. GOES sends to the RF dish only. Jason-3 receives no collector uplinks; the optical downlink is inactive.',
      'This is an illustrative relay route, not a Jason-3 mission link. In the mission reference, GOES DCS relays messages from compatible remote platforms; Jason-3 uses its direct RF ground link.',
    ],
    sources: ['dcs', 'cnes', 'jason'],
  },
};

export function sceneEmphasis(key: string, step: number | null): number {
  if (step === null) return 1;
  if (key === 'relay') return step === 3 ? 1 : 0.06;
  if (key === 'field-sensor') return step <= 3 ? 1 : 0.06;
  if (key === 'acquisition') return step === 1 ? 1 : step === 2 || step === 3 ? 0.9 : 0.06;
  if (key === 'reception') return step === 2 || step === 3 || step === 4 ? 1 : step === 5 ? 0.7 : 0.06;
  if (key === 'optical') return step === 2 ? 1 : 0.06;
  if (key === 'container' || key === 'container-rx') return step === 4 || step === 5 ? 1 : 0.06;
  if (key === 'container-tx') return step === 4 || step === 5 ? 0.28 : 0.06;
  if (key === 'container-interior') return step === 4 || step === 5 ? 0.7 : 0.06;
  if (key === 'processing') return step >= 5 && step <= 8 ? 1 : 0.06;
  if (key === 'archive') return step >= 8 ? 1 : 0.06;
  if (key === 'cloud') return step === 9 ? 0.35 : step >= 8 ? 1 : 0.06;
  if (key === 'users') return step === 10 ? 1 : 0.06;
  return 0.06;
}

export function linkVisibleInStage(id: string, step: number | null): boolean {
  if (step === null) return true;
  if (step === 2) return id === 'downlink';
  // The receiver routes in steps 4/5 terminate on the actual rack equipment.
  if (step === 5) return id === 'ifl';
  if (step === 8) return id === 'archive' || id === 'cloud';
  if (step === 9) return id === 'archive';
  if (step === 10) return id === 'distribution' || id === 'dod';
  return false;
}
